import * as React from 'react';
import type { Tooltip } from 'recharts';
import { cn } from '@/shared/lib/utils';
import { Text } from '@/shared/ui/text/components/Text';
import { getPayloadConfigFromPayload, useChart } from './types';

type ChartTooltipContentProps = React.ComponentProps<typeof Tooltip> &
  React.ComponentProps<'div'> & {
    hideLabel?: boolean;
    hideIndicator?: boolean;
  };

/**
 * チャートツールチップのコンテンツ
 *
 * Recharts の Tooltip content として使用する。
 * ChartConfig に基づいてラベルと色インジケーターを自動表示する。
 */
export const ChartTooltipContent = React.forwardRef<HTMLDivElement, ChartTooltipContentProps>(
  (
    { active, payload, className, hideLabel = false, hideIndicator = false, label, formatter },
    ref,
  ) => {
    const { config } = useChart();

    if (!active || !payload?.length) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(
          'grid min-w-32 items-start gap-1.5 rounded-lg border border-border/50 bg-background bg-white px-2.5 py-1.5 text-xs shadow-xl',
          className,
        )}
      >
        {!hideLabel && typeof label === 'string' && (
          <Text variant="description" className="font-medium">
            {config[label as keyof typeof config]?.label || label}
          </Text>
        )}
        <div className="grid gap-1.5">
          {payload
            .filter((item) => item.type !== 'none')
            .map((item, index) => {
              const key = `${item.name || item.dataKey || 'value'}`;
              const itemConfig = getPayloadConfigFromPayload(config, item, key);
              const indicatorColor = item.payload?.fill || item.color;

              return (
                <div key={item.dataKey} className="flex w-full items-center gap-2">
                  {formatter && item?.value !== undefined && item.name ? (
                    formatter(item.value, item.name, item, index, item.payload)
                  ) : (
                    <>
                      {!hideIndicator && (
                        <div
                          className="h-2.5 w-2.5 shrink-0 rounded-xs"
                          style={{ backgroundColor: indicatorColor }}
                        />
                      )}
                      <div className="flex flex-1 items-center justify-between gap-2">
                        <Text variant="description" color="muted">
                          {itemConfig?.label || item.name}
                        </Text>
                        {item.value != null && (
                          <Text variant="description" viewMode="number">
                            {item.value}
                          </Text>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
        </div>
      </div>
    );
  },
);
ChartTooltipContent.displayName = 'ChartTooltipContent';
